import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { modelsApi } from '../../api/models';
import tierService from '../../services/tierService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface TierModelAccessManagerProps {
  model: any;
  onClose: () => void;
}

export const TierModelAccessManager: React.FC<TierModelAccessManagerProps> = ({
  model,
  onClose,
}) => {
  const queryClient = useQueryClient();

  // Fetch all tiers
  const { data: tiers = [] } = useQuery({
    queryKey: ['tiers'],
    queryFn: () => tierService.getAllTiers(false),
  });

  // Fetch all tier access for this model
  const { data: allTierAccess = [] } = useQuery({
    queryKey: ['all-tier-model-access', model.id],
    queryFn: async () => {
      const accessPromises = tiers.map((tier: any) =>
        modelsApi.getTierModelAccess(tier.id)
      );
      const results = await Promise.all(accessPromises);
      return results.flat();
    },
    enabled: tiers.length > 0,
  });

  // State for each tier
  const [tierStates, setTierStates] = useState<Record<string, {
    hasAccess: boolean;
    monthlyLimit: number;
    isDefault: boolean;
  }>>({});

  // Initialize states when data loads
  React.useEffect(() => {
    const newStates: Record<string, any> = {};
    tiers.forEach((tier: any) => {
      const access = allTierAccess.find((a: any) => 
        a.tier_id === tier.id && a.model_id === model.id
      );
      newStates[tier.id] = {
        hasAccess: !!access,
        monthlyLimit: access?.monthly_limit ?? -1,
        isDefault: access?.is_default ?? false,
      };
    });
    setTierStates(newStates);
  }, [tiers, allTierAccess, model.id]);

  // Grant/Update access mutation
  const grantAccessMutation = useMutation({
    mutationFn: ({ tierId, data }: { tierId: string; data: any }) =>
      modelsApi.grantTierModelAccess(tierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-tier-model-access'] });
      queryClient.invalidateQueries({ queryKey: ['tier-model-access'] });
      // Don't show toast here - will be shown by handleSaveAll
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    },
  });

  // Revoke access mutation
  const revokeAccessMutation = useMutation({
    mutationFn: ({ tierId, modelId }: { tierId: string; modelId: string }) =>
      modelsApi.revokeTierModelAccess(tierId, modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-tier-model-access'] });
      queryClient.invalidateQueries({ queryKey: ['tier-model-access'] });
      // Don't show toast here - will be shown by handleSaveAll
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    },
  });

  const updateTierState = (tierId: string, field: string, value: any) => {
    setTierStates(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        [field]: value,
      },
    }));
  };

  const handleSaveAll = async () => {
    const promises = tiers.map((tier: any) => {
      const state = tierStates[tier.id];
      if (!state) return Promise.resolve();

      const existingAccess = allTierAccess.find((a: any) => 
        a.tier_id === tier.id && a.model_id === model.id
      );

      if (!state.hasAccess && existingAccess) {
        return revokeAccessMutation.mutateAsync({ tierId: tier.id, modelId: model.id });
      } else if (state.hasAccess) {
        return grantAccessMutation.mutateAsync({
          tierId: tier.id,
          data: {
            tier_id: tier.id,
            model_id: model.id,
            monthly_limit: state.monthlyLimit,
            is_default: state.isDefault,
          },
        });
      }
      return Promise.resolve();
    });

    try {
      await Promise.all(promises);
      toast.success('All tier access settings saved');
      onClose(); // Close the dialog after successful save
    } catch (error) {
      // Individual errors are already handled by mutations
    }
  };

  const handleUpdateAccess = (tierId: string) => {
    const state = tierStates[tierId];
    if (!state) return;

    // Check if access currently exists in the database
    const existingAccess = allTierAccess.find((a: any) => 
      a.tier_id === tierId && a.model_id === model.id
    );

    if (!state.hasAccess && existingAccess) {
      // Revoke access only if it exists
      revokeAccessMutation.mutate({ tierId, modelId: model.id });
    } else if (state.hasAccess) {
      // Grant or update access
      grantAccessMutation.mutate({
        tierId,
        data: {
          tier_id: tierId,
          model_id: model.id,
          monthly_limit: state.monthlyLimit,
          is_default: state.isDefault,
        },
      });
    } else {
      // No action needed - trying to disable something that doesn't exist
      toast.success('No changes needed');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Tier Access for {model.display_name}
          </DialogTitle>
        </DialogHeader>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Configure Tier Access</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tier</TableHead>
                  <TableHead>Has Access</TableHead>
                  <TableHead>Monthly Limit</TableHead>
                  <TableHead>Default</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map((tier: any) => {
                  const state = tierStates[tier.id] || {
                    hasAccess: false,
                    monthlyLimit: -1,
                    isDefault: false,
                  };

                  return (
                    <TableRow key={tier.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{tier.display_name}</div>
                          <div className="text-sm text-muted-foreground">
                            {tier.name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={state.hasAccess}
                          onCheckedChange={(checked) =>
                            updateTierState(tier.id, 'hasAccess', checked)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {state.hasAccess && (
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              value={state.monthlyLimit}
                              onChange={(e) =>
                                updateTierState(tier.id, 'monthlyLimit', parseInt(e.target.value))
                              }
                              className="w-24"
                            />
                            <span className="text-sm text-muted-foreground">
                              (-1 = unlimited)
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {state.hasAccess && (
                          <Switch
                            checked={state.isDefault}
                            onCheckedChange={(checked) =>
                              updateTierState(tier.id, 'isDefault', checked)
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            onClick={handleSaveAll}
            disabled={grantAccessMutation.isPending || revokeAccessMutation.isPending}
          >
            {(grantAccessMutation.isPending || revokeAccessMutation.isPending) ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
